import React, { useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { observer } from 'mobx-react';
import {
  Alert,
  Button,
  Card,
  CardBody,
  CheckboxInput,
  FormLabelGroup
} from '@appfolio/react-gears';
import { withEnvRoot } from '@tportal/common/utils';
import { activate } from '@tportal/login/api/activations';
import CompanyInfoFooter from '@tportal/login/components/CompanyInfoFooter';
import CompanyLogo from '@tportal/login/components/CompanyLogo';
import PasswordValidator from '@tportal/login/components/PasswordValidator';
import ShowPasswordInput from '@tportal/login/components/ShowPasswordInput';
import { useAlert } from '@tportal/login/hooks/use_alert';
import i18n from '@tportal/login/i18n';

interface Props {
  isHOA: boolean;
  email?: string;
  logoUrl?: string;
  pmInfo: any;
}

interface Params {
  activationToken: string;
}

const containerClass = (
  `d-flex flex-column h-100 w-100 p-0 m-0 top-0 start-0 pb-5 overflow-auto${isMobile ? '' : ' pt-5'}`
);
const cardClass = isMobile ? 'start-0 top-0 w-100 m-0 align-items-center py-4' :
  'align-items-center justify-content-center py-4 font-weight-light start-50 translate-middle-x';
const cardBodyClass = isMobile ? 'px-4 w-100' : 'mx-3';

export const ActivationPage = (
  {
    email,
    isHOA,
    logoUrl,
    pmInfo,
  }: Props
) => {
  const history = useHistory();
  const { activationToken }: Params = useParams();
  const { search } = useLocation();
  const query = new URLSearchParams(search);

  const emailSplit = email?.split('@');
  let truncatedEmail: string | undefined;

  if (emailSplit) {
    const truncatedUserName =
      emailSplit[0].length > 10
        ? `${emailSplit[0].substr(0, 10)}....`
        : emailSplit[0];

    const truncatedDomain =
      emailSplit[1].length > 20
        ? `${emailSplit[1].substr(0, 20)}....`
        : emailSplit[1];

    truncatedEmail = `${truncatedUserName}@${truncatedDomain}`;
  }

  const { alert, setAlert } = useAlert();

  const [isChecked, setIsChecked] = useState(false);
  const [password, setPassword] = useState<string | null>(null);
  const [isValidPassword, setIsValidPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const termsAndConditionStatement = (
    <small
      dangerouslySetInnerHTML={{
        __html: i18n.t('messages.activation_terms_of_service',
          {
            terms_of_service_link: i18n.t('messages.terms_of_service_link'),
            privacy_policy_link: i18n.t('messages.privacy_policy_link')
          })
      }}
    />);
  const handleCheckboxKeyDown = (event: any) => {
    if (event.key === 'Enter') {
      setIsChecked(!isChecked);
    }
  };

  const handleActivation = async (): Promise<void> => {
    setLoading(true);
    try {
      const { type, message } = await activate(
        activationToken,
        password ?? '',
        {
          email_id: query.get('email_id'),
          sms_id: query.get('sms_id'),
          portfolio_uuid: query.get('portfolio_uuid'),
          tpa_type: query.get('tpa_type'),
          version: query.get('version'),
          button_text: query.get('button_text'),
          button_color: query.get('button_color')
        });

      if (type === 'new_unit_added') {
        setAlert({ type: 'success', message: message ?? '' });
        history.replace(withEnvRoot('/users/sign_in'));
      } else {
        setAlert(null);
        history.replace(withEnvRoot('/'));
      }
    } catch (e) {
      setLoading(false);
      if (e.status === 410) {
        setAlert({ type: 'danger', message: i18n.t('messages.already_used_activation') });
        history.replace(withEnvRoot('/users/sign_in'));
      } else {
        setAlert({ type: 'danger', message: i18n.t('errors.general_error') });
      }
    }
  };

  const handleButtonKeyDown = async (event: any) => {
    if (event.key === 'Enter') {
      await handleActivation();
    }
  };

  return (
    <div className='top-0 start-0 w-100 m-0 p-0 h-100'>
      <div className='login-page__body'>
        <div className={containerClass}>
          <Card className={cardClass} style={isMobile ? { width: window.innerWidth } : { width: '400px' }}>
            <CompanyLogo
              companyName={pmInfo.name}
              logoUrl={logoUrl}
            />
            <h2 className='mt-3 mb-0 fw-light u-text-center'>
              {i18n.t('labels.activate_your_online_portal')}
            </h2>
            <CardBody className={cardBodyClass}>
              {alert &&
                <Alert color={alert.type}>
                  {alert.message}
                </Alert>
              }
              <FormLabelGroup
                className='d-flex justify-content-center'
                label={i18n.t('labels.email')}
                stacked
              >
                {truncatedEmail}
              </FormLabelGroup>
              <FormLabelGroup
                className='d-flex pt-1 justify-content-center'
                label={i18n.t('labels.create_password')}
                stacked
                feedback={(password !== null) && !isValidPassword}
              >
                <ShowPasswordInput
                  password={password}
                  setPassword={setPassword}
                  inputAriaLabel='Create a new password with at least 8 characters, at least one letter, at least one number, and at least one symbol'
                />
                <PasswordValidator
                  value={password}
                  setIsValidPassword={setIsValidPassword}
                />
              </FormLabelGroup>
              <CheckboxInput
                checkboxLabel={termsAndConditionStatement}
                onChange={() => setIsChecked(!isChecked)}
                onKeyDown={handleCheckboxKeyDown}
                value={isChecked}
                aria-label='By checking this box, I certify that I agree to the Terms of Service and Privacy Policy'
                data-testid='activation-checkbox'
              />
              <Button
                className='js-activate-account-react mt-2 fw-light'
                color='primary'
                block
                disabled={(!isChecked || !isValidPassword || loading)}
                onClick={async () => handleActivation()}
                onKeyDown={handleButtonKeyDown}
              >
                {i18n.t('actions.activate_account')}
              </Button>
            </CardBody>
          </Card>
        </div>
        <CompanyInfoFooter pmInfo={pmInfo} isHOA={isHOA} />
      </div>
    </div>
  );
};

export default observer(ActivationPage);
